/**
 * H2 — the feedback modal's rules, as a pure module.
 *
 * The repo has no DOM renderer (OD-60), so anything that branches lives here
 * with tests rather than inside a component nobody can assert on.
 */

export const RATING_MIN = 1
export const RATING_MAX = 5
export const NPS_MIN = 0
export const NPS_MAX = 10

/**
 * ⚠️ MUST MATCH `backend/src/modules/feedback/dto/feedback.dto.ts`. The packages
 * share generated *types*, not constants, so this is a second copy and the
 * server is authoritative — a client that allowed more would produce a 400 the
 * parent cannot act on, and one that allowed less would silently truncate what
 * they wrote.
 */
export const COMMENT_MAX = 2000

export type Draft = {
  rating: number | null
  nps: number | null
  comment: string
}

export const EMPTY_DRAFT: Draft = { rating: null, nps: null, comment: '' }

export type DraftProblem = 'NO_RATING' | 'COMMENT_TOO_LONG'

/**
 * 🔴 ONLY THE RATING IS REQUIRED.
 *
 * NPS and the comment are optional in the schema, and making either mandatory
 * here would be a client inventing a rule the server does not have — with the
 * predictable result that a parent who wanted to give five stars and leave is
 * blocked, and gives nothing.
 */
export function validateDraft(draft: Draft): DraftProblem[] {
  const problems: DraftProblem[] = []
  if (draft.rating === null) problems.push('NO_RATING')
  if (draft.comment.trim().length > COMMENT_MAX) problems.push('COMMENT_TOO_LONG')
  return problems
}

export function canSubmit(draft: Draft): boolean {
  return validateDraft(draft).length === 0
}

/**
 * Draft → request body.
 *
 * 🔴 AN UNANSWERED NPS IS OMITTED, NOT SENT AS 0. `Feedback.nps` is nullable
 * precisely so a non-response can be told apart from a detractor; a client that
 * defaulted it would make every parent who skipped the question the most
 * negative answer on the scale, and the score would be wrong in a way nobody
 * could see from the data.
 *
 * ⚠️ An empty comment is omitted for the same reason — a stored blank is a row
 * that looks like it has a comment until you open it.
 */
export function toSubmission(draft: Draft): { rating: number; nps?: number; comment?: string } {
  if (draft.rating === null) {
    throw new Error('toSubmission called on a draft with no rating — guard with canSubmit')
  }
  const comment = draft.comment.trim()
  return {
    rating: draft.rating,
    ...(draft.nps === null ? {} : { nps: draft.nps }),
    ...(comment.length === 0 ? {} : { comment }),
  }
}

/** `1 … 5`, so the component does not build the scale itself. */
export const RATING_SCALE: readonly number[] = Array.from(
  { length: RATING_MAX - RATING_MIN + 1 },
  (_, i) => RATING_MIN + i,
)

/** `0 … 10`. NPS is a defined instrument; the range is not ours to trim. */
export const NPS_SCALE: readonly number[] = Array.from(
  { length: NPS_MAX - NPS_MIN + 1 },
  (_, i) => NPS_MIN + i,
)

export function charactersLeft(draft: Draft): number {
  return COMMENT_MAX - draft.comment.trim().length
}
